import React from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Radar, Bot, Film, BarChart3, Sparkles, ShieldCheck, Cpu } from "lucide-react";
import { Button } from "../components/ui/button";

const CELLS = [
  { icon: Radar, title: "Cultural Intelligence", body: "Signals from licensed feeds, scored by velocity, saturation, longevity, risk, and brand fit." },
  { icon: Bot, title: "50+ Specialist Agents", body: "Executive orchestration, writing, visual, audio, editing, publishing, analytics, safety." },
  { icon: Film, title: "Automated Video Studio", body: "Trend → script → storyboard → scenes → publish. Every step versioned and auditable." },
  { icon: Sparkles, title: "AI Influencer Engine", body: "Identity bibles, visual references, memory, and evolution — unlimited virtual talent." },
  { icon: BarChart3, title: "Attribution Analytics", body: "From cultural signal to published asset to platform metric. Zero gaps." },
  { icon: ShieldCheck, title: "Compliance Vault", body: "Copyright, moderation, disclosure, and rights baked into every render." },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-[#FAFAFA] film-grain">
      {/* Nav */}
      <nav className="border-b border-[#141414] px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#F5A524] rounded-sm flex items-center justify-center">
            <span className="font-display font-black text-black">A</span>
          </div>
          <div>
            <div className="font-display font-bold text-[15px]">AIMOS</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Media Operating System</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Sign in</Link>
          <Button data-testid="nav-signup" onClick={() => nav("/signup")} className="bg-[#F5A524] hover:bg-[#D98218] text-black font-medium rounded-sm">
            Launch workspace
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]" />
        <div className="relative max-w-7xl mx-auto px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#222] bg-[#0c0c0c] rounded-sm"
            >
              <span className="w-1.5 h-1.5 bg-[#17C964] rounded-full pulse-amber" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">System operational · v0.1</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            >
              The operating system<br />
              for <span className="text-[#F5A524]">AI-native</span> media<br />
              at industrial scale.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="text-lg text-[#A1A1AA] max-w-2xl leading-relaxed"
            >
              A fleet of collaborating AI agents that research, write, render, publish, analyze, and continuously improve
              thousands of original short-form pieces across every brand, character, and channel you operate.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
              className="flex flex-wrap items-center gap-3 pt-2"
            >
              <Button data-testid="hero-start-btn" onClick={() => nav("/signup")} className="bg-[#F5A524] hover:bg-[#D98218] text-black font-medium h-11 px-6 rounded-sm">
                Provision a tenant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-signin-btn" onClick={() => nav("/login")} variant="outline" className="h-11 px-6 rounded-sm border-[#222] bg-transparent hover:bg-[#0f0f0f] hover:text-white">
                Sign in
              </Button>
              <div className="flex items-center gap-2 pl-4 ml-4 border-l border-[#222] text-[11px] font-mono uppercase text-[#52525B]">
                <Cpu className="w-3 h-3" /> 58 agents · 12 workflows · &lt; 1.4s median latency
              </div>
            </motion.div>
          </div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-square border border-[#222] rounded-sm bg-[#0a0a0a] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1643388019656-34a8006477c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxjeWJlcnB1bmslMjBkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3ODM0NTI3ODd8MA&ixlib=rb-4.1.0&q=85"
                alt="AIMOS Intelligence Radar"
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 border border-[#F5A524]/40 bg-black/70 backdrop-blur-md p-4 rounded-sm">
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#F5A524] mb-1">Live · Trend Radar</div>
                <div className="font-display font-bold text-lg">6 opportunities detected</div>
                <div className="text-[11px] text-[#A1A1AA] mt-1">Signals normalized across 14 cultural feeds · scored, deduped, brand-matched.</div>
              </div>
              <div className="absolute top-4 right-4 px-2 py-1 bg-black/70 border border-[#222] rounded-sm">
                <span className="font-mono text-[9px] uppercase text-[#00F0FF]">SIGNAL LOCK</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-8 py-20">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B] mb-2">// Platform Surface</div>
            <h2 className="font-display font-bold text-4xl">Every stage of the pipeline is an agent, a policy, and an audit line.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#171717] border border-[#171717]">
          {CELLS.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors duration-200 group">
                <Icon className="w-6 h-6 text-[#F5A524] mb-6" strokeWidth={1.5} />
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mb-2">0{i + 1} / 06</div>
                <h3 className="font-display font-bold text-xl mb-3">{c.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-8 py-16 border-t border-[#141414]">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-bold text-3xl mb-2">Provision your first tenant.</h3>
            <p className="text-[#A1A1AA]">White-label out of the box. Multi-brand from day one. Every decision auditable.</p>
          </div>
          <Button data-testid="footer-start-btn" onClick={() => nav("/signup")} className="bg-[#F5A524] hover:bg-[#D98218] text-black font-medium h-11 px-8 rounded-sm">
            Launch workspace <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-[#141414] px-8 py-6 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">AIMOS · 0.1.0 · Cinematic Technical</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">All rights reserved</div>
      </footer>
    </div>
  );
}
